import { useEffect, type ReactNode } from 'react';
import { Icon } from './Icon';

interface SheetProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Every editing flow lives in one of these. On a phone it is the bottom sheet
 * from the design, thumb-reachable and animating up from the edge; from `md` up
 * a sheet glued to the bottom of a wide monitor reads as a mistake, so it
 * becomes a centred dialog with the same content.
 */
export function Sheet({ open, title, subtitle, onClose, children }: SheetProps) {
  // Escape closes, and the page behind must not scroll while a sheet is up.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 mx-auto max-w-app md:flex md:max-w-none md:items-center md:justify-center md:p-6">
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 animate-fade-in bg-[rgba(11,15,30,0.45)]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute inset-x-0 bottom-0 flex max-h-[88%] animate-sheet-up flex-col
                   rounded-t-[26px] bg-white px-4 pb-7 pt-5 shadow-sheet
                   md:relative md:inset-auto md:max-h-[85dvh] md:w-full md:max-w-lg
                   md:animate-fade-in md:rounded-3xl md:px-6 md:pb-6 md:shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[17px] font-semibold leading-tight text-conduril-ink">{title}</div>
            {subtitle && (
              <div className="num mt-1.5 text-[11px] tracking-wide text-conduril-muted">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-conduril-border
                       bg-[#fbfbfd] text-[#5c6373]"
          >
            <Icon name="close" className="text-[17px]" />
          </button>
        </div>
        <div className="-mx-4 mt-4 flex-1 overflow-y-auto px-4 md:-mx-6 md:px-6">{children}</div>
      </div>
    </div>
  );
}
